import React from "react"
import autobind from "class-autobind"
import Dialog from "material-ui/Dialog"
import TextField from "material-ui/TextField"
import FlatButton from "material-ui/FlatButton"
import strings from "../../localization/strings"
import Clear from "../misc/Clear"
import { Table, TableBody, TableFooter, TableHeader, TableHeaderColumn, TableRow, TableRowColumn, CircularProgress } from "material-ui"
import FontIcon from "material-ui/FontIcon"
import IconButton from "material-ui/IconButton"
import ImageLoader from "../misc/ImageLoader"
import { IFilterSupervisorDTO, ISupervisor, ISupervisorSearch } from "../../models"
import AuthenticationStore from "../../stores/AuthenticationStore"
import * as api from "../../api"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import AccountStore from "../../stores/AccountStore"
import _ from "lodash"

interface IProps {
  selectedSupervisorIds: string[]
  canChooseYourSelf: boolean
  supervisorRestrictHierarchyId: string
  employeePermissionHierarchy: string
  handleClose(): void
  handleUpdateSupervisor(selectedSupervisor: ISupervisor): void
}
interface IState {
  loading: boolean
  selectedSupervisor: ISupervisor
  filteredSupervisors: ISupervisor[]
  search: string
  offset: number
  total: number
}
const styles = {
  columnValue: {
    textAlign: "center",
    cursor: "pointer"
  },
  columnHeader: {
    textAlign: "center"
  },
  imageColumn: {
    width: 30,
    textAlign: "center"
  },
  footerContent: {
    float: "right"
  }
}

export default class AddSupervisorDialogForChip extends React.Component<IProps, IState> {
  private limit: number
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      filteredSupervisors: [],
      search: "",
      offset: 0,
      total: 0,
      selectedSupervisor: null
    }
    autobind(this)
    const tempLimit = Math.floor(window.innerHeight / 50) - 9
    this.limit = tempLimit > 8 ? 8 : tempLimit
  }

  componentDidMount() {
    this.getFilteredSupervisors()
  }

  getFilteredSupervisors() {    
    const accessHierarchyIds = this.props.supervisorRestrictHierarchyId !== undefined ?
      [this.props.supervisorRestrictHierarchyId] :
      this.props.employeePermissionHierarchy ?
        hierarchyHelper.getHierarchyIdsForShowAllChildHierarchyData(true, this.props.employeePermissionHierarchy) :
        null

    const filterSupervisorDTO: IFilterSupervisorDTO = {
      ExcludeSupervisorIds: this.props.selectedSupervisorIds,
      AccessHierarchyIds: accessHierarchyIds
    }

    this.setState({ loading: true, selectedSupervisor: null }, () =>
      api.getSupervisorSearch(AccountStore.getCurrentAccount().Id, filterSupervisorDTO, this.state.search, this.state.offset, this.limit).then(res => {
        const result = res.data as ISupervisorSearch
        this.setState({
          loading: false,
          filteredSupervisors: result.Supervisors,
          total: result.TotalCount
        })
      })
    )
  }

  offsetChange(offset) {
    this.setState({ offset }, () => this.getFilteredSupervisors())
  }

  handleSearch = _.debounce(() => this.getFilteredSupervisors(), 1000)

  render() {
    return (
      <Dialog
        open
        modal
        title={strings.Select_Supervisor}
        actions={[
          <span style={{ fontSize: 19, marginRight: 75, color: "#F57C00" }}>
            {!this.props.canChooseYourSelf && this.state.selectedSupervisor && this.state.selectedSupervisor.Id === AuthenticationStore.getLoggedInUser().Id &&
              strings.You_cant_choose_yourself_as_your_supervisor
            }
          </span>,
          <FlatButton
            data-intercom-target={"Btn-Close-Supervisor-Selection-Dialog"}
            label={strings.Cancel}
            secondary
            onClick={this.props.handleClose}
          />,
          <FlatButton
            data-intercom-target={"Btn-Save-Selected-Supervisor"}
            label={strings.OK}
            primary
            keyboardFocused
            disabled={
              this.state.selectedSupervisor === null ||
              (!this.props.canChooseYourSelf && this.state.selectedSupervisor.Id === AuthenticationStore.getLoggedInUser().Id)
            }
            onClick={() => this.props.handleUpdateSupervisor(this.state.selectedSupervisor)}
          />
        ]}
        onRequestClose={this.props.handleClose}
      >
        <div data-intercom-target={"Supervisor-Selection-Dialog"}>
          <div style={{ padding: 20, paddingLeft: 35 }}>
            <i style={{ fontSize: 25 }} className="fa fa-search" />
            <TextField
              style={{ marginRight: 20, width: "90%", marginLeft: 15 }}
              hintText={strings.search}
              value={this.state.search}
              onChange={e =>
                this.setState({
                  search: e.target.value,
                  offset: 0
                }, () => this.handleSearch())
              }
            />
            <Clear />
          </div>
          <div style={{ textAlign: "center", height: 500 }}>
            {this.state.loading ?
              <CircularProgress /> :
              <Table
                onCellClick={rowNumber => this.setState({ selectedSupervisor: this.state.filteredSupervisors[rowNumber] })}
                multiSelectable={false}
              >
                <TableHeader>
                  <TableRow>
                    <TableHeaderColumn style={styles.imageColumn} />
                    <TableHeaderColumn style={styles.columnHeader}>{strings.Name}</TableHeaderColumn>
                    <TableHeaderColumn style={styles.columnHeader}>{strings.Employee_ID}</TableHeaderColumn>
                  </TableRow>
                </TableHeader>
                <TableBody showRowHover={true} deselectOnClickaway={false}>
                  {this.state.filteredSupervisors.map((row, i) =>
                    <TableRow key={row.Id} selected={this.state.selectedSupervisor && this.state.selectedSupervisor.Id === row.Id}>
                      <TableRowColumn style={styles.imageColumn}>
                        {(row.ProfileImageS3Url || row.ProfileImageID) &&
                          <ImageLoader url={row.ProfileImageS3Url} fixedHeight={75} avatar={true} src={"/users/profileimage/" + row.ProfileImageS3Url} key={`smallImg-${i}`} />
                        }
                      </TableRowColumn>
                      <TableRowColumn style={styles.columnValue} title={`${row.FirstName} ${row.LastName}`}>
                        {`${row.FirstName} ${row.LastName}`}
                      </TableRowColumn>
                      <TableRowColumn style={styles.columnValue}>{row.ExternalId}</TableRowColumn>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow className="TableFooter" style={{ borderBottom: 0 }}>
                    <TableRowColumn style={styles.footerContent}>
                      <IconButton disabled={this.state.offset === 0} onClick={() => this.offsetChange(0)}>
                        <FontIcon className="fa fa-step-backward" />
                      </IconButton>
                      <IconButton disabled={this.state.offset === 0} onClick={() => this.offsetChange(this.state.offset - this.limit)}>
                        <FontIcon className="fa fa-chevron-left" />
                      </IconButton>
                      <IconButton disabled={this.state.offset + this.limit >= this.state.total} onClick={() => this.offsetChange(this.state.offset + this.limit)}>
                        <FontIcon className="fa fa-chevron-right" />
                      </IconButton>
                      <IconButton disabled={this.state.offset + this.limit >= this.state.total} onClick={() => this.state.total % this.limit === 0 ? this.offsetChange(this.state.total - this.limit) : this.offsetChange(this.state.total - (this.state.total % this.limit))}>
                        <FontIcon className="fa fa-step-forward" />
                      </IconButton>
                    </TableRowColumn>
                  </TableRow>
                </TableFooter>
              </Table>
            }
          </div>
        </div>
      </Dialog>
    )
  }
}
